import {computed, isRef, nextTick, ref, unref, watch} from 'vue';
import ApiService from '@/utils/ApiService';
import {OVERVIEW_SORT} from '@/utils/OverviewSort';

/**
 * Page size used by the overviews. Small enough that the first screen
 * arrives quickly, large enough that a normal viewport is filled by one
 * request — a page that does not fill the viewport never triggers the
 * scroll that would ask for the next one.
 */
export const DEFAULT_PER_PAGE = 30;

/** How long to wait for the user to stop typing before asking the server. */
export const SEARCH_DEBOUNCE_MS = 250;

const STORAGE_PREFIX = 'kontext.list';

function resolve(value) {
	if (typeof value === 'function' && !isRef(value)) return value();
	return unref(value);
}

/**
 * The identity of the current browser-history entry.
 *
 * Vue Router numbers the entries it pushes, so going back to a page lands
 * on the same number and finds the state that page saved. Anything else
 * — a fresh tab, a reload — is a different entry and starts clean.
 */
function historyEntryId() {
	try {
		const position = window.history.state?.position;
		return position === undefined ? 'new' : String(position);
	} catch {
		return 'new';
	}
}

function readStored(key) {
	try {
		const stored = window.sessionStorage.getItem(key);
		return stored ? JSON.parse(stored) : null;
	} catch {
		// A private window, cleared site data, or a browser refusing storage.
		return null;
	}
}

function writeStored(key, value) {
	try {
		window.sessionStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Losing the scroll position is not worth breaking the page over.
	}
}

/**
 * Page a REST collection, appending as the user scrolls.
 *
 * The quick filter and the sort order are the server's job: filtering the
 * items already fetched would only search the part of the collection that
 * happens to have been loaded, which is a different question than the one
 * the user asked.
 *
 * :param options.url: Collection URL; a string, ref or getter.
 * :param options.params: Extra query parameters; an object or getter.
 * :param options.perPage: Page size.
 * :param options.stateKey: Enables browser-history retention under this
 *     name. Omit for lists that should always start fresh.
 * :param options.transform: Reworks one page of results before they are
 *     appended — media assets, for instance, are cross-referenced against
 *     the map's points as they arrive.
 * :param options.sort: Initial sort order.
 * :returns: The list state and the actions over it.
 */
export default function useInfiniteList(options = {}) {
	const {
		url,
		params = () => ({}),
		perPage = DEFAULT_PER_PAGE,
		stateKey = null,
		transform = null,
		sort: initialSort = OVERVIEW_SORT.NAME_ASC
	} = options;

	const items = ref([]);
	const total = ref(0);
	const loading = ref(false);
	const loadingMore = ref(false);
	const error = ref(null);
	const search = ref('');
	const sort = ref(initialSort);

	const hasMore = computed(() => items.value.length < total.value);

	// Every request carries a token; only the newest one may write to the
	// list. Without this a slow answer to an abandoned search overwrites
	// the results of the search the user actually waited for.
	let token = 0;
	let pending = null;
	let debounce = null;
	let scrollElement = null;
	let restoreScrollTop = null;
	// A restore assigns the stored filter and sort order. That is not the
	// user changing them, so it must not set the watchers below going: the
	// load doing the restoring would be followed by a second, identical one.
	let restoring = false;

	const storageKey = stateKey
		? `${STORAGE_PREFIX}.${stateKey}.${historyEntryId()}`
		: null;

	function requestParams(page, size) {
		const extra = resolve(params) || {};
		const query = {...extra, page, per_page: size};
		const term = search.value.trim();
		if (term) query.q = term;
		if (sort.value) query.sort = sort.value;
		return query;
	}

	async function fetchPage(page, size) {
		const {data} = await ApiService.get(resolve(url), {
			params: requestParams(page, size)
		});
		const results = data?.results ?? [];
		return {
			results: transform ? transform(results) : results,
			total: data?.meta?.total ?? 0
		};
	}

	/**
	 * Load the list from the beginning.
	 *
	 * On the first call after a history restore this fetches as many items
	 * as were on screen before, so returning to the overview shows the same
	 * list rather than its first page.
	 */
	async function load() {
		const restored = takeRestoredState();
		const wanted = restored?.count || perPage;
		const mine = ++token;
		loading.value = true;
		error.value = null;
		try {
			const pages = [];
			for (let page = 1; page <= Math.ceil(wanted / perPage); page += 1) {
				pages.push(await fetchPage(page, perPage));
				if (token !== mine) return;
				if (pages[pages.length - 1].results.length < perPage) break;
			}
			if (token !== mine) return;
			items.value = pages.flatMap(one => one.results);
			total.value = pages.length ? pages[pages.length - 1].total : 0;
			if (restored) applyRestoredScroll(restored);
		} catch (caught) {
			if (token === mine) error.value = caught;
		} finally {
			if (token === mine) loading.value = false;
		}
	}

	/** Append the next page, if there is one and none is already on its way. */
	async function loadMore() {
		if (pending || loading.value || !hasMore.value) return pending ?? undefined;
		const mine = token;
		const nextPage = Math.floor(items.value.length / perPage) + 1;
		loadingMore.value = true;
		pending = (async () => {
			try {
				const page = await fetchPage(nextPage, perPage);
				if (token !== mine) return;
				items.value = [...items.value, ...page.results];
				total.value = page.total;
			} catch (caught) {
				if (token === mine) error.value = caught;
			} finally {
				pending = null;
				if (token === mine) loadingMore.value = false;
				saveState();
			}
		})();
		return pending;
	}

	/** Resolve once no request started by a filter or sort change is left. */
	async function settled() {
		await nextTick();
		if (debounce) {
			await new Promise(done => setTimeout(done, SEARCH_DEBOUNCE_MS + 10));
		}
		await pending;
		await nextTick();
	}

	function addItem(item) {
		items.value = [...items.value, item];
		total.value += 1;
		saveState();
	}

	function removeItem(predicate) {
		const index = items.value.findIndex(predicate);
		if (index < 0) return;
		items.value.splice(index, 1);
		total.value = Math.max(0, total.value - 1);
		saveState();
	}

	function replaceItem(predicate, item) {
		const index = items.value.findIndex(predicate);
		if (index >= 0) items.value[index] = item;
	}

	// --- browser history ---------------------------------------------------

	/** Remember the list element whose scroll position should be restored. */
	function trackScroll(element) {
		if (scrollElement === element) return;
		if (scrollElement?.removeEventListener) {
			scrollElement.removeEventListener('scroll', saveState);
		}
		scrollElement = element || null;
		if (scrollElement?.addEventListener) {
			scrollElement.addEventListener('scroll', saveState, {passive: true});
		}
		if (scrollElement && restoreScrollTop !== null) {
			applyScrollTop(restoreScrollTop);
		}
	}

	/** Store what a back-navigation needs to rebuild this view. */
	function saveState() {
		if (!storageKey) return;
		writeStored(storageKey, {
			count: items.value.length,
			search: search.value,
			sort: sort.value,
			scrollTop: scrollElement ? scrollElement.scrollTop : 0
		});
	}

	function takeRestoredState() {
		if (!storageKey) return null;
		const stored = readStored(storageKey);
		if (!stored) return null;
		restoring = true;
		if (typeof stored.search === 'string') search.value = stored.search;
		if (stored.sort) sort.value = stored.sort;
		nextTick(() => {
			restoring = false;
		});
		return stored;
	}

	function applyScrollTop(scrollTop) {
		if (!scrollElement) return;
		scrollElement.scrollTop = scrollTop;
		restoreScrollTop = null;
	}

	function applyRestoredScroll(stored) {
		const scrollTop = stored.scrollTop || 0;
		if (!scrollTop) return;
		restoreScrollTop = scrollTop;
		// The items have to be rendered before the container is tall enough
		// to scroll to where the user was.
		nextTick(() => applyScrollTop(scrollTop));
	}

	/** Forget the stored state, so the next visit starts at the top. */
	function clearState() {
		if (!storageKey) return;
		try {
			window.sessionStorage.removeItem(storageKey);
		} catch {
			// Nothing to do — the state is advisory.
		}
	}

	function dispose() {
		if (debounce) clearTimeout(debounce);
		trackScroll(null);
	}

	watch(sort, () => {
		if (restoring) return;
		saveState();
		load();
	});

	watch(search, () => {
		if (restoring) return;
		// Debounced: a request per keystroke would mostly be answers nobody
		// is waiting for any more.
		if (debounce) clearTimeout(debounce);
		debounce = setTimeout(() => {
			debounce = null;
			saveState();
			load();
		}, SEARCH_DEBOUNCE_MS);
	});

	return {
		items,
		total,
		hasMore,
		loading,
		loadingMore,
		error,
		search,
		sort,
		load,
		loadMore,
		settled,
		addItem,
		removeItem,
		replaceItem,
		trackScroll,
		saveState,
		clearState,
		dispose
	};
}
